import * as React from 'react';
import { CardAside, CardEmpty } from '@chess/api-dashboard/react';
import { SkeletonPlot, TextSkeleton, TIME_CLASS_LABEL, type CardProps } from '@chess/api-insights/charts';
import type { GameOutcome } from '@chess/api-model';
import type { AccuracyPoint, AccuracyTrendResult } from './AccuracyTrendAggregator.ts';
import { dateLabel, dayAxis, dayOffsets } from '@chess/api-insights/days';
import { accuracyScale } from './scale.ts';

export type AccuracyTrendCardProps = CardProps<AccuracyTrendResult & { from: number; to: number }, { onOpen?: (reviewPath: string) => void }>;

const round = (a: number) => Math.round(a);
const RESULT: Record<GameOutcome, string> = { win: 'Won', draw: 'Drawn', loss: 'Lost' };

const WIDTH = 320;
const HEIGHT = 112;
const PAD = { left: 26, right: 12, top: 12, bottom: 18 };
/** Horizontal gap, in chart units, between games played on the same day. */
const SPREAD = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

/** "0:42" from seconds. */
const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

/** "vs bob · Sep 14 · Blitz · Lost · accuracy 52 · a win slipped at 0:42 (34.Rf3)": what a point's tooltip and label say. */
function describe(p: AccuracyPoint): string {
  const slip = p.timeSlip && `a win slipped at ${clock(p.timeSlip.seconds)} left (${Math.ceil(p.timeSlip.ply / 2)}${p.timeSlip.ply % 2 ? '.' : '…'}${p.timeSlip.san})`;
  return [p.opponent && `vs ${p.opponent}`, dateLabel(p.at), p.timeClass && TIME_CLASS_LABEL[p.timeClass], RESULT[p.outcome], `accuracy ${round(p.accuracy)}`, slip]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The reader's accuracy per scanned game over the period: a dot per game
 * (date across, accuracy 0–100 up), no line between them, the period's
 * average as a dashed rule. A dot's tooltip names the game, and a click
 * opens its review.
 * One neutral colour for every dot, except games where a winning position
 * slipped with under a minute on the reader's clock (`timeSlip`), in the
 * time colour and named in a legend, to spot time trouble at a glance.
 * The body of the card: the section draws its frame and title
 * (`registerCard`, @chess/api-dashboard).
 */
export const AccuracyTrendCard: React.FC<AccuracyTrendCardProps> = (props) => {
  const [active, setActive] = React.useState<number>();
  if (props.loading)
    return (
      <>
        <CardAside>Last 30 days</CardAside>
        <div className="stat">
          <span className="stat-value">
            <TextSkeleton sample="Average 79" />
          </span>
        </div>
        <div className="lines accuracy-plot">
          <SkeletonPlot width={WIDTH} height={HEIGHT} label="Loading the accuracy per game" />
        </div>
      </>
    );
  const { points, average, from, to, onOpen } = props;
  if (!points.length)
    return (
      <>
        <CardAside>Last 30 days</CardAside>
        <CardEmpty>No scanned games in the last 30 days.</CardEmpty>
      </>
    );
  // Each dot sits in the column of the local day it was played, the day its
  // label and tooltip name (@chess/api-insights/days).
  const axis = dayAxis(from, to, PAD.left, WIDTH - PAD.right);
  const x = axis.centre;
  // Fitted to the games' own distribution (scale.ts): their range takes most of the height.
  const scale = accuracyScale(points.map((p) => p.accuracy));
  const y = (a: number) => PAD.top + scale.depth(a) * (HEIGHT - PAD.top - PAD.bottom);
  const dx = dayOffsets(points.map((p) => p.at), from, axis.column, SPREAD);
  const open = (p: AccuracyPoint) => (p.reviewPath && onOpen ? () => onOpen(p.reviewPath!) : undefined);
  const shown = active === undefined ? undefined : points[active];
  const dots = points.map((p, i) => ({ x: x(p.at) + dx[i], y: y(p.accuracy) }));
  return (
    <>
      <CardAside>Last 30 days</CardAside>
      <div className="stat">
        <span className="stat-value">{`Average ${round(average ?? 0)}`}</span>
      </div>
      <div className="lines accuracy-plot">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={`Accuracy per game (non-linear scale, fitted to the games' spread): ${points.map((p) => `${dateLabel(p.at)} ${round(p.accuracy)}`).join(', ')}`}>
          {scale.ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--viz-grid)" strokeWidth={1} />
              <text x={PAD.left - 4} y={y(t) + 3} textAnchor="end">
                {t}
              </text>
            </g>
          ))}
          <text className="accuracy-scale-note" x={WIDTH - PAD.right - 2} y={y(0) - 3} textAnchor="end" aria-hidden="true">
            non-linear scale
          </text>
          {[from, from + Math.floor((axis.days - 1) / 2) * DAY_MS, to].map((at, k) => (
            <text key={k} x={x(at)} y={HEIGHT - 4} textAnchor={k === 0 ? 'start' : k === 2 ? 'end' : 'middle'}>
              {dateLabel(at)}
            </text>
          ))}
          {average !== undefined && (
            <line className="accuracy-average" x1={PAD.left} x2={WIDTH - PAD.right} y1={y(average)} y2={y(average)}>
              <title>{`Average ${round(average)}`}</title>
            </line>
          )}
          {points.map((p, i) => {
            const go = open(p);
            const { x: cx, y: cy } = dots[i];
            return (
              <g
                key={p.id}
                className={`accuracy-point${go ? ' accuracy-point-link' : ''}${active === i ? ' accuracy-point-active' : ''}`}
                role={go ? 'link' : undefined}
                tabIndex={go ? 0 : undefined}
                aria-label={go ? `Open the review: ${describe(p)}` : describe(p)}
                data-track={go ? 'overview.accuracy.point' : undefined}
                onClick={go}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(undefined)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(undefined)}
                onKeyDown={
                  go
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          go();
                        }
                      }
                    : undefined
                }>
                <circle className="chart-hit" cx={cx} cy={cy} r={8} />
                <circle className={`accuracy-dot${p.timeSlip ? ' accuracy-dot-time' : ''}`} cx={cx} cy={cy} r={3.5} />
              </g>
            );
          })}
        </svg>
        {shown && (
          <div
            className="accuracy-tip"
            role="tooltip"
            style={{ left: `${(dots[active!].x / WIDTH) * 100}%`, top: `${(dots[active!].y / HEIGHT) * 100}%` }}>
            {describe(shown)}
          </div>
        )}
      </div>
      {points.some((p) => p.timeSlip) && (
        <ul className="legend-row accuracy-legend">
          <li>
            <span className="legend-swatch accuracy-swatch-time" aria-hidden="true" />
            A win slipped with under a minute left
          </li>
        </ul>
      )}
    </>
  );
};
